import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Loader2, CheckCircle, Clock, Trash2, ChevronRight, Copy,
  Search, Check, X, ArrowLeft, FileText, MessageSquare, ListPlus, Sparkles, Lightbulb,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { NextMeetingSection } from '../components/NextMeetingSection';
import { PlaudImportDialog } from '../components/PlaudImportDialog';
import { PlaudSettingsDialog } from '../components/PlaudSettingsDialog';
import { AudioLines, Settings, Landmark } from 'lucide-react';
import { MeetingEmailSettingsDialog } from '../components/MeetingEmailSettingsDialog';
import { MeetingAttendanceCard } from '../components/MeetingAttendanceCard';
import { NotesMarkdown } from '../components/NotesMarkdown';
import { PassReasonPicker, passReasonText } from '../components/PassReasonPicker';
import { CollapsibleSection } from '../components/CollapsibleSection';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
};

const textToHtml = (text) =>
  (text || '').split('\n').map(l => `<p>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br/>'}</p>`).join('');

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.innerText || '').trim();
};

const STAGE_COLORS = {
  ai_reviewed: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  olta_reviewed: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  olta_approved: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  outreach_wip: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  contacted: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  first_call_meeting: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  nda: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  on_site: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  loi_signed: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  due_diligence: 'text-blue-400 bg-blue-400/10 border-violet-400/20',
  signing: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  closing: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  passed: 'text-red-400 bg-red-400/10 border-red-400/20',
};

const DECISION_OPTIONS = [
  { value: 'advance', label: 'Advance', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' },
  { value: 'pass', label: 'Pass', color: 'text-red-400 bg-red-400/10 border-red-500/30' },
];

function getDecisionStyle(decision) {
  return DECISION_OPTIONS.find(d => d.value === decision)?.color || '';
}

export default function TeamMeetingPage() {
  const navigate = useNavigate();
  const { isAdmin, isAcMember } = useAuth();
  const [agenda, setAgenda] = useState([]);
  const [nextAgenda, setNextAgenda] = useState([]);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null); // null = auto (backend decides by 16:00 cutoff)
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [meetingSettings, setMeetingSettings] = useState(null);
  const [showPastEmails, setShowPastEmails] = useState(false);
  const [intakeFrozen, setIntakeFrozen] = useState(false);
  const [autoFrozen, setAutoFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [addCustomOpen, setAddCustomOpen] = useState(false);
  const [emailLang, setEmailLang] = useState('en');
  const [deals, setDeals] = useState([]);

  // Load user language preference
  useEffect(() => {
    api.getUserPreferences().then(r => { if (r.data.lang) setEmailLang(r.data.lang); }).catch(() => {});
  }, []);
  const [dealSearch, setDealSearch] = useState('');
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  // Pass reason dialog
  const [passDialog, setPassDialog] = useState(null); // {itemId, projectId, projectName}
  const [passReason, setPassReason] = useState('');
  const [passTags, setPassTags] = useState([]);
  const [passBench, setPassBench] = useState(false);
  const [passSaving, setPassSaving] = useState(false);
  const [addingAcDeals, setAddingAcDeals] = useState(false);
  const [limitPrompt, setLimitPrompt] = useState(null); // {projectId, message}
  // Meeting summary note (non-deal topics)
  const [summaryNote, setSummaryNote] = useState('');
  const [savedSummary, setSavedSummary] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  // Discussed result dialog
  const [discussedDialog, setDiscussedDialog] = useState(null); // {item}
  const [discussedResult, setDiscussedResult] = useState('');
  const [discussedSaving, setDiscussedSaving] = useState(false);
  // Report view
  const [history, setHistory] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [view, setView] = useState('agenda');
  const [plaudOpen, setPlaudOpen] = useState(false);
  const [plaudInbox, setPlaudInbox] = useState([]);
  const [plaudInboxItem, setPlaudInboxItem] = useState(null);
  const [plaudSettingsOpen, setPlaudSettingsOpen] = useState(false);

  const loadPlaudInbox = useCallback(() => {
    if (isAcMember) return;
    api.getPlaudInbox().then(r => setPlaudInbox(r.data || [])).catch(() => {});
  }, [isAcMember]);
  useEffect(() => { loadPlaudInbox(); }, [loadPlaudInbox]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plaud = params.get('plaud');
    if (!plaud) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (plaud === 'connected') {
      toast.success('Plaud account connected — auto-import is ready');
      setPlaudSettingsOpen(true);
    } else {
      toast.error('Plaud connection failed — please try again');
    }
  }, []);

  const handleDismissPlaud = async (id) => {
    try { await api.dismissPlaudInbox(id); loadPlaudInbox(); }
    catch { toast.error('Failed to dismiss'); }
  };

  const loadAgenda = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, nxt] = await Promise.all([api.getMeetingAgenda('current'), api.getMeetingAgenda('next')]);
      setAgenda(cur.data);
      setNextAgenda(nxt.data);
    } catch { toast.error('Failed to load agenda'); }
    setLoading(false);
  }, []);

  const loadHistory = useCallback(async () => {
    try { const res = await api.getMeetingHistory(); setHistory(res.data); } catch {}
  }, []);

  const loadEmailSettings = useCallback(async () => {
    try { const res = await api.getMeetingEmailSettings(); setEmailSettings(res.data); } catch {}
  }, []);

  const loadMeetingSettings = useCallback(async () => {
    try { const res = await api.getMeetingSettings(); setMeetingSettings(res.data); } catch {}
  }, []);

  useEffect(() => { loadAgenda(); loadHistory(); loadMeetingSettings(); }, [loadAgenda, loadHistory, loadMeetingSettings]);
  useEffect(() => { if (isAdmin) loadEmailSettings(); }, [isAdmin, loadEmailSettings]);
  useEffect(() => {
    api.getIntakeFreeze().then(res => {
      setIntakeFrozen(!!res.data?.enabled);
      setAutoFrozen(!!res.data?.auto_frozen);
    }).catch(() => {});
  }, []);

  const toggleIntakeFreeze = async () => {
    try {
      const res = await api.setIntakeFreeze(!intakeFrozen);
      setIntakeFrozen(!!res.data.enabled);
      toast.success(res.data.enabled
        ? 'Deal intake frozen — no new deals can be added to meeting agendas'
        : 'Deal intake resumed — new deals can be added again');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change intake freeze'); }
  };

  const loadSummaryNote = useCallback(() => {
    api.getMeetingSummaryNote()
      .then(r => {
        const val = r.data.summary_html || r.data.summary || '';
        setSummaryNote(val); setSavedSummary(val);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadSummaryNote(); }, [loadSummaryNote]);

  const handleSaveSummary = async () => {
    setSavingSummary(true);
    try {
      await api.saveMeetingSummaryNote(stripHtml(summaryNote), summaryNote);
      setSavedSummary(summaryNote);
      toast.success('Meeting summary saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSavingSummary(false);
  };

  // ─── Handlers ────────────────────────────
  const openAddDealDialog = async (target = null) => {
    setAddTarget(target);
    setAddDealOpen(true);
    setLoadingDeals(true);
    setDealSearch('');
    try { const res = await api.listProjects(); setDeals(res.data || []); }
    catch { toast.error('Failed to load deals'); }
    setLoadingDeals(false);
  };

  const handleAddDeal = async (projectId, force = false) => {
    try {
      const res = await api.addToAgenda(projectId, force, addTarget);
      const landed = res.data?.bucket;
      if (landed === 'next' && addTarget !== 'next') {
        toast.info("Today's agenda email is already out — deal queued for the following meeting");
      } else {
        toast.success(landed === 'next' ? 'Deal queued for the following meeting' : 'Deal added to the agenda');
      }
      setAddDealOpen(false);
      setLimitPrompt(null);
      loadAgenda();
    } catch (err) {
      if (err.response?.status === 409 && !force) {
        setLimitPrompt({ projectId, message: err.response?.data?.detail });
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleAddCustom = async () => {
    if (!customTitle.trim()) { toast.error('Enter a title'); return; }
    try {
      await api.addCustomAgendaItem(customTitle, customNotes, addTarget);
      toast.success('Agenda item added');
      setAddCustomOpen(false);
      setCustomTitle('');
      setCustomNotes('');
      loadAgenda();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemove = async (itemId) => {
    try { await api.removeFromAgenda(itemId); loadAgenda(); }
    catch { toast.error('Failed to remove'); }
  };

  const handleToggleStatus = async (item) => {
    if (item.status === 'to_discuss') {
      // Show discussion result dialog before marking as discussed — prefilled with
      // existing notes (e.g. from a Plaud import) so nothing has to be retyped
      setDiscussedDialog(item);
      const existingHtml = (item.notes_html || '').trim();
      const existingPlain = (item.notes || '').trim();
      setDiscussedResult(existingHtml || (existingPlain ? existingPlain.split('\n').map(l => `<p>${l || '<br>'}</p>`).join('') : ''));
      return;
    }
    // Toggling back to to_discuss — no popup needed
    try {
      await api.updateAgendaStatus(item.id, 'to_discuss');
      setAgenda(prev => prev.map(a => a.id === item.id ? { ...a, status: 'to_discuss' } : a));
    } catch { toast.error('Failed to update'); }
  };

  const handleConfirmDiscussed = async () => {
    const plain = stripHtml(discussedResult);
    if (!discussedDialog || !plain) { toast.error('Please enter the discussion result'); return; }
    setDiscussedSaving(true);
    try {
      // Marks discussed + saves notes (plain + rich HTML) + posts deal comment + logs a
      // "Team Meeting" Meeting/Call note on the deal
      await api.discussAgendaItem(discussedDialog.id, plain, discussedResult);
      toast.success('Marked as discussed — note posted to the deal');
      setDiscussedDialog(null);
      loadAgenda();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setDiscussedSaving(false);
  };

  const handleDecision = async (itemId, decision, item) => {
    // If "pass" is selected, show reason dialog first
    if (decision === 'pass' && item?.project_id) {
      setPassDialog({ itemId, projectId: item.project_id, projectName: item.project_name });
      setPassReason('');
      setPassBench(false);
      return;
    }
    try {
      const res = await api.updateAgendaDecision(itemId, decision);
      setAgenda(prev => prev.map(a => a.id === itemId ? { ...a, decision } : a));
      const sr = res.data?.stage_result;
      if (decision === 'advance' && sr) {
        if (sr.error) {
          toast.warning(`Decision saved, but stage not advanced: ${sr.error}`);
        } else if (sr.status === 'approval_proposed' || sr.status === 'contact_proposed') {
          toast.info(sr.message);
        } else if (sr.bypassed_approval) {
          toast.success(`Advanced to ${sr.next_stage_label || 'next stage'} — final immediately (both approvers present)`);
        } else {
          toast.success(`Advanced to ${sr.next_stage_label || 'next stage'}`);
        }
        loadAgenda();
      } else {
        const label = DECISION_OPTIONS.find(d => d.value === decision)?.label || decision;
        toast.success(`Decision: ${label}`);
      }
    } catch { toast.error('Failed to set decision'); }
  };

  const togglePassTag = (tid) => {
    setPassTags(prev => {
      const next = prev.includes(tid) ? prev.filter(x => x !== tid) : [...prev, tid];
      setPassReason(passReasonText(next));
      return next;
    });
  };

  const handleConfirmPass = async () => {
    if (!passDialog || !passReason.trim()) { toast.error('Please enter a reason'); return; }
    setPassSaving(true);
    try {
      // 1. Propose pass on the deal (final immediately when both approvers are present in the meeting)
      const tdRes = await api.turnDownProject(passDialog.projectId, passReason, passBench, 'meeting', passTags);
      // 2. Set the decision — the item stays under "To Discuss" so the team can still
      //    tick the checkmark and record discussion notes
      await api.updateAgendaDecision(passDialog.itemId, 'pass');
      setAgenda(prev => prev.map(a => a.id === passDialog.itemId
        ? { ...a, decision: 'pass' }
        : a));
      if (tdRes.data?.status === 'passed') {
        toast.success(`${passDialog.projectName} passed — final immediately (both approvers present)`);
      } else {
        toast.success(`Pass proposed for ${passDialog.projectName}${passBench ? ' (Bench)' : ''} — awaiting confirmation`);
      }
      setPassDialog(null);
      setPassTags([]);
      setPassReason('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to pass deal'); }
    setPassSaving(false);
  };

  const handleAcFlag = async (item) => {
    const next = !item.ac_flagged;
    try {
      await api.setAcFlag(item.project_id, next);
      setAgenda(prev => prev.map(a => a.id === item.id ? { ...a, ac_flagged: next } : a));
      toast.success(next ? 'Earmarked for AC meeting (!)' : 'AC earmark removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddAcDeals = async () => {
    setAddingAcDeals(true);
    try {
      const res = await api.addAcDealsToAgenda();
      const { added, total_flagged, limit_reached } = res.data;
      if (total_flagged === 0) toast.info('No deals are earmarked with "!" yet');
      else if (limit_reached) toast.warning(`Added ${added} — the agenda deal limit is reached, remaining "!" deals were skipped`);
      else if (added === 0) toast.info('All "!" deals are already on the agenda');
      else toast.success(`Added ${added} AC deal${added !== 1 ? 's' : ''} to the agenda`);
      if (added > 0) loadAgenda();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setAddingAcDeals(false);
  };

  const handleSaveNotes = async (itemId) => {
    try {
      const plain = stripHtml(notesValue);
      await api.updateAgendaNotes(itemId, plain, plain ? notesValue : '');
      setAgenda(prev => prev.map(a => a.id === itemId ? { ...a, notes: plain, notes_html: plain ? notesValue : '' } : a));
      setEditingNotes(null);
    } catch { toast.error('Failed to save notes'); }
  };

  const handleFinalize = async (plaudOverride) => {
    const override = plaudOverride === true;
    setFinalizing(true);
    try {
      const res = await api.finalizeMeeting(emailLang, override);
      const { archived, removed_discussed, promoted, carried_over, report_id } = res.data;
      toast.success(
        `Meeting finalized — ${archived ? 'summary archived, ' : ''}${removed_discussed} discussed cleared` +
        `${carried_over ? `, ${carried_over} rolled over` : ''}${promoted ? `, ${promoted} queued item${promoted !== 1 ? 's' : ''} promoted` : ''}`
      );
      setFinalizeConfirm(false);
      setSummaryNote(''); setSavedSummary('');
      loadAgenda(); loadHistory();
      api.getIntakeFreeze().then(r => { setIntakeFrozen(!!r.data?.enabled); setAutoFrozen(!!r.data?.auto_frozen); }).catch(() => {});
      if (archived && report_id) handleViewReport(report_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'plaud_missing') {
        if (window.confirm('No Plaud summary has been applied for this meeting yet. If a recording exists, sync & review it first.\n\nFinalize anyway?')) {
          setFinalizing(false);
          return handleFinalize(true);
        }
      } else {
        toast.error(detail || 'Failed to finalize meeting');
      }
    }
    setFinalizing(false);
  };

  const handleClearDiscussed = async () => {
    setClearing(true);
    try { const res = await api.clearDiscussed(); toast.success(`Cleared ${res.data.removed} items`); loadAgenda(); }
    catch { toast.error('Failed to clear'); }
    setClearing(false);
  };

  const handleViewReport = async (reportId) => {
    try { const res = await api.getMeetingReport(reportId); setActiveReport(res.data); setView('report'); }
    catch { toast.error('Failed to load report'); }
  };

  const handleDeleteReport = async (reportId) => {
    try { await api.deleteMeetingReport(reportId); toast.success('Deleted'); loadHistory(); if (activeReport?.id === reportId) setView('agenda'); }
    catch { toast.error('Failed to delete'); }
  };

  const handleCopy = () => {
    if (!activeReport) return;
    // Copy as rich HTML for email clients
    const blob = new Blob([activeReport.body], { type: 'text/html' });
    const plainBlob = new Blob([activeReport.body.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
    navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })]);
    setCopied(true); toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTranslate = async () => {
    if (!activeReport) return;
    setTranslating(true);
    try {
      const res = await api.translateToGerman(activeReport.body);
      setActiveReport(prev => ({ ...prev, body: res.data.translated }));
      toast.success('Translated to German');
    } catch (err) { toast.error(err.response?.data?.detail || 'Translation failed'); }
    setTranslating(false);
  };


  // ─── Loading ────────────────────────────
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  // ─── Report View ────────────────────────
  if (view === 'report' && activeReport) {
    const isPreMeeting = activeReport.report_type === 'pre_meeting';
    return (
      <div className="space-y-4 max-w-5xl" data-testid="meeting-report-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setView('agenda')} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl text-foreground tracking-tight">
              {isPreMeeting ? 'Pre-Meeting Email' : 'Meeting Summary'}
            </h1>
            {activeReport.name && <p className="text-sm text-foreground font-medium mt-0.5" data-testid="report-meeting-name">{activeReport.name}</p>}
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date(activeReport.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {activeReport.deal_count != null && ` — ${activeReport.deal_count} deals`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleCopy} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4">
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy Email</>}
            </Button>
          </div>
        </div>
        <div className="bg-white border border-border rounded-sm p-3 sm:p-6 overflow-x-auto">
          <div className="text-sm text-gray-800 leading-relaxed" data-testid="meeting-email-body"
            dangerouslySetInnerHTML={{ __html: activeReport.body }} />
        </div>
      </div>
    );
  }

  // ─── Agenda View ────────────────────────
  const slaRank = (a) => {
    const s = a.sla;
    if (!s || s.days_left == null) return [2, 0];
    if (s.escalated || s.days_left < 0) return [0, s.days_left];
    return [1, s.days_left];
  };
  const toDiscuss = agenda.filter(a => a.status === 'to_discuss').sort((x, y) => {
    const [rx, dx] = slaRank(x); const [ry, dy] = slaRank(y);
    return rx !== ry ? rx - ry : dx - dy;
  });
  const discussed = agenda.filter(a => a.status === 'discussed');
  const agendaProjectIds = new Set([...agenda, ...nextAgenda].filter(a => a.project_id).map(a => a.project_id));
  const filteredDeals = deals.filter(d =>
    !agendaProjectIds.has(d.id) && (!dealSearch || d.name.toLowerCase().includes(dealSearch.toLowerCase()))
  );

  return (
    <div className="space-y-6 max-w-5xl" data-testid="team-meeting-page">
      {intakeFrozen && (
        <div className="flex items-center justify-between gap-2 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2 text-xs text-amber-300"
          data-testid="meeting-intake-freeze-banner">
          <div>
            <span className="font-bold">⏸ Deal intake paused</span>
            <span className="text-amber-300/80">
              {autoFrozen
                ? ' — automatic: the previous meeting was not finalized by agenda-email time. The agenda email is on hold; finalizing the meeting resumes intake and sends it.'
                : ' — adding new deals to the agenda is temporarily disabled.'}
            </span>
          </div>
          <button onClick={toggleIntakeFreeze} data-testid="meeting-intake-resume-btn"
            className="shrink-0 h-7 px-3 rounded-sm bg-amber-600/20 border border-amber-500/40 text-amber-300 hover:bg-amber-600/30 font-medium transition-colors">
            ▶ Resume intake
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight">Team Meeting</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {agenda.length} items on agenda — {toDiscuss.length} to discuss, {discussed.length} discussed
          </p>
          <p className="text-[11px] text-muted-foreground/70 mt-1" data-testid="meeting-workflow-hint">
            Flow: add deals during the day → agenda auto-emailed at {emailSettings?.send_time || '16:00'} (Mon–Fri) → meeting → tick off discussed deals with notes → fill the Meeting Summary → Finalize.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!intakeFrozen && (
            <Button onClick={toggleIntakeFreeze} data-testid="meeting-intake-freeze-btn"
              variant="outline" title="Pause deal intake — no new deals can be added to meeting agendas until resumed"
              className="border-amber-600/40 text-amber-400 hover:bg-amber-600/10 rounded-sm text-xs h-8 px-3">
              ⏸ Freeze intake
            </Button>
          )}
          <Button onClick={() => openAddDealDialog()} data-testid="add-deal-to-agenda"
            variant="outline" className="border-border text-foreground rounded-sm text-xs h-8 px-3">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Deal
          </Button>
          <Button onClick={() => { setAddTarget(null); setAddCustomOpen(true); }} data-testid="add-custom-agenda-item"
            variant="outline" className="border-border text-foreground rounded-sm text-xs h-8 px-3">
            <ListPlus className="w-3.5 h-3.5 mr-1" /> Add Agenda Item
          </Button>
          {!isAcMember && (
            <>
              <Button onClick={() => setPlaudOpen(true)} data-testid="plaud-import-btn"
                variant="outline" title="Paste a Plaud meeting summary — AI fills deal notes and the Meeting Summary for review"
                className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-xs h-8 px-3">
                <AudioLines className="w-3.5 h-3.5 mr-1" /> Plaud Import
              </Button>
              <Button onClick={() => setPlaudSettingsOpen(true)} data-testid="plaud-settings-btn"
                variant="outline" title="Plaud connection & auto-import — settings"
                className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-xs h-8 px-2">
                <Settings className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
          <Button onClick={handleAddAcDeals} disabled={addingAcDeals} data-testid="add-ac-deals-btn"
            variant="outline" className="border-amber-600/40 text-amber-400 hover:bg-amber-600/10 rounded-sm text-xs h-8 px-3">
            {addingAcDeals ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Add AC Deals to Agenda
          </Button>
          {meetingSettings?.can_finalize && (
            <Button onClick={() => setFinalizeConfirm(true)} disabled={finalizing} data-testid="finalize-meeting-btn"
              className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8 px-3">
              {finalizing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
              Finalize Meeting
            </Button>
          )}
        </div>
      </div>

      {/* Auto-skip banner: last meeting fell out, queue moved onto the agenda */}
      {meetingSettings?.last_auto_skip && (
        <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 rounded-sm px-3 py-2.5"
          data-testid="auto-skip-banner">
          <span className="text-base leading-none">⏭️</span>
          <p className="text-xs text-amber-400">
            {meetingSettings.last_auto_skip.skips > 1
              ? `${meetingSettings.last_auto_skip.skips} Meetings ausgefallen`
              : 'Letztes Meeting ausgefallen'}
            {' — '}
            {meetingSettings.last_auto_skip.promoted === 1
              ? '1 Deal ist aus der Warteschlange nachgerückt'
              : `${meetingSettings.last_auto_skip.promoted} Deals sind aus der Warteschlange nachgerückt`}
            {' '}(automatisch, nichts war besprochen). In der Agenda-Mail sind sie als „⏭️ Nachgerückt" markiert.
          </p>
        </div>
      )}

      {/* New Plaud summaries from the user's own connected Plaud account — awaiting review */}
      {!isAcMember && plaudInbox.length > 0 && (
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-sm" data-testid="plaud-inbox-banner">
          <div className="px-4 py-2.5 border-b border-violet-500/20 flex items-center gap-2">
            <AudioLines className="w-4 h-4 text-violet-400" />
            <p className="text-sm font-medium text-violet-300">
              {plaudInbox.length === 1 ? 'New Plaud summary received' : `${plaudInbox.length} new Plaud summaries received`}
            </p>
          </div>
          {plaudInbox.map(m => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-violet-500/10 last:border-b-0"
              data-testid={`plaud-inbox-item-${m.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">
                  {m.subject}
                  {m.is_own === false && m.owner_name && (
                    <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 align-middle"
                      data-testid={`plaud-inbox-owner-${m.id}`}>
                      von {m.owner_name}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {m.from_name || m.from_email}
                  {m.received_at && ` — ${new Date(m.received_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                  {m.status === 'ready' ? ' · AI-parsed, ready for review' : ' · not parsed yet — opens as raw text'}
                </p>
              </div>
              {m.is_own !== false && (
                <Button onClick={() => { setPlaudInboxItem(m); setPlaudOpen(true); }}
                  data-testid={`plaud-inbox-review-${m.id}`}
                  className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-7 px-3">
                  Review
                </Button>
              )}
              <button onClick={() => handleDismissPlaud(m.id)}
                title={m.is_own === false ? `Dismiss (Admin — Owner: ${m.owner_name || 'unbekannt'})` : 'Dismiss'}
                data-testid={`plaud-inbox-dismiss-${m.id}`}
                className="text-muted-foreground/40 hover:text-red-400 transition-colors p-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attendance — who is in the room (Point 5: Max & Talu present ⇒ decisions final immediately) */}
      <MeetingAttendanceCard />

      {/* Meeting Summary — non-deal topics (top, filled during post-processing) */}
      <div className="bg-card border border-border rounded-sm" data-testid="meeting-summary-box">
        <div className="px-4 py-3 border-b border-border">
          <p className="font-heading text-base text-foreground">Meeting Summary</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Generic meeting notes (non-deal topics) — appears at the top of the archived Meeting Summary. Cleared on Finalize.
          </p>
        </div>
        <div className="p-4 space-y-2">
          <div data-testid="meeting-summary-input">
            <ReactQuill theme="snow" value={summaryNote}
              onChange={(val, _delta, source) => {
                setSummaryNote(val);
                if (source !== 'user') setSavedSummary(val);
              }}
              modules={QUILL_MODULES}
              placeholder="e.g. Fund update discussed, new NDA template agreed, next AC meeting scheduled for 15 Aug..." />
          </div>
          <p className="text-[10px] text-muted-foreground">Pasted formatting (bold, bullets, ...) is kept.</p>
          <div className="flex items-center justify-end gap-2">
            {summaryNote !== savedSummary && <span className="text-[10px] text-amber-400">Unsaved changes</span>}
            <Button onClick={handleSaveSummary} disabled={savingSummary || summaryNote === savedSummary}
              data-testid="save-meeting-summary-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
              {savingSummary && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Save Summary
            </Button>
          </div>
        </div>
      </div>

      {/* To Discuss */}
      <CollapsibleSection
        storageKey="meeting.to-discuss"
        icon={Clock}
        iconClass="text-amber-400"
        title={`To Discuss (${toDiscuss.length})`}
        testId="to-discuss-section"
      >
        {toDiscuss.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No items to discuss. Add deals or agenda items above.</p>
        ) : toDiscuss.map(item => (
          <AgendaItem key={item.id} item={item} navigate={navigate}
            onToggle={() => handleToggleStatus(item)}
            onRemove={() => handleRemove(item.id)}
            onDecision={(d) => handleDecision(item.id, d, item)}
            onAcFlag={() => handleAcFlag(item)}
            editingNotes={editingNotes} notesValue={notesValue}
            onStartEditNotes={() => { setEditingNotes(item.id); setNotesValue(item.notes_html || textToHtml(item.notes || '')); }}
            onChangeNotes={setNotesValue}
            onSaveNotes={() => handleSaveNotes(item.id)}
            onCancelNotes={() => setEditingNotes(null)}
          />
        ))}
      </CollapsibleSection>

      {/* Discussed */}
      {discussed.length > 0 && (
        <CollapsibleSection
          storageKey="meeting.discussed"
          icon={CheckCircle}
          iconClass="text-emerald-400"
          title={`Discussed (${discussed.length})`}
          testId="discussed-section"
          right={isAdmin && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleClearDiscussed(); }} disabled={clearing}
              data-testid="clear-discussed-btn"
              className="text-xs text-muted-foreground hover:text-red-400 h-7">
              <Trash2 className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        >
          {discussed.map(item => (
            <AgendaItem key={item.id} item={item} navigate={navigate}
              onToggle={() => handleToggleStatus(item)}
              onRemove={() => handleRemove(item.id)}
              onDecision={(d) => handleDecision(item.id, d, item)}
              onAcFlag={() => handleAcFlag(item)}
              editingNotes={editingNotes} notesValue={notesValue}
              onStartEditNotes={() => { setEditingNotes(item.id); setNotesValue(item.notes_html || textToHtml(item.notes || '')); }}
              onChangeNotes={setNotesValue}
              onSaveNotes={() => handleSaveNotes(item.id)}
              onCancelNotes={() => setEditingNotes(null)}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* Following Meeting queue */}
      <NextMeetingSection
        items={nextAgenda}
        isAdmin={isAdmin}
        navigate={navigate}
        emailSettings={emailSettings}
        onAddDeal={() => openAddDealDialog('next')}
        onAddCustom={() => { setAddTarget('next'); setAddCustomOpen(true); }}
        onRemove={handleRemove}
        onOpenSettings={() => setEmailSettingsOpen(true)}
      />

      {/* AC Meeting moved to its own page */}
      <button onClick={() => navigate('/ac-meeting')} data-testid="ac-meeting-moved-link"
        className="w-full bg-card border border-purple-500/25 rounded-sm px-4 py-3 flex items-center gap-2 text-left hover:bg-purple-500/5 transition-colors">
        <Landmark className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-foreground font-medium">AC Meeting</span>
        <span className="text-xs text-muted-foreground">— Agenda, Teaser-Vorbereitung, Plaud-Nachbericht &amp; Archiv auf der eigenen Seite</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
      </button>

      {/* Meeting Summaries (archive) + collapsed agenda emails */}
      {history.length > 0 && (() => {
        const summaries = history.filter(r => r.report_type === 'meeting_summary');
        const preEmails = history.filter(r => !['meeting_summary', 'ac_meeting_summary', 'ac_meeting'].includes(r.report_type));
        const renderRow = (r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/10 group"
            data-testid={`history-row-${r.id}`}>
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-foreground">
                {r.name ? r.name : (r.report_type === 'pre_meeting' ? 'Agenda Email' : 'Meeting Summary')}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono">
                {new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {r.deal_count != null && ` — ${r.deal_count} deals`}
                {r.auto_sent && ' · auto-sent'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleViewReport(r.id)}
              className="h-7 text-[11px] text-muted-foreground hover:text-foreground">View</Button>
            {isAdmin && (
              <button onClick={() => handleDeleteReport(r.id)}
                className="text-muted-foreground/30 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
        return (
          <CollapsibleSection
            storageKey="meeting.summaries"
            icon={FileText}
            iconClass="text-muted-foreground"
            title="Meeting Summaries"
            count={summaries.length}
            defaultOpen={false}
            testId="meeting-history"
          >
            <div className="px-4 py-2 border-b border-border">
              <p className="text-[11px] text-muted-foreground">Archived meetings — created when a meeting is finalized.</p>
            </div>
            {summaries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-5">No finalized meetings yet.</p>
            ) : summaries.map(renderRow)}
            {preEmails.length > 0 && (
              <div className="border-t border-border">
                <button onClick={() => setShowPastEmails(v => !v)} data-testid="toggle-past-emails"
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showPastEmails ? 'rotate-90' : ''}`} />
                  Sent agenda emails ({preEmails.length})
                </button>
                {showPastEmails && preEmails.map(renderRow)}
              </div>
            )}
          </CollapsibleSection>
        );
      })()}

      {/* Add Deal Dialog */}
      <Dialog open={addDealOpen} onOpenChange={setAddDealOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[70vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground">
            Add Deal to {addTarget === 'next' ? 'the Following Meeting' : 'Agenda'}
          </DialogTitle></DialogHeader>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Search deals..." value={dealSearch} onChange={e => setDealSearch(e.target.value)}
              className="pl-8 bg-secondary border-border text-foreground rounded-sm h-9 text-sm" data-testid="agenda-deal-search" />
          </div>
          <div className="flex-1 overflow-y-auto mt-2 space-y-1" style={{ maxHeight: '400px' }}>
            {loadingDeals ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : filteredDeals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No deals available to add</p>
            ) : filteredDeals.map(d => (
              <button key={d.id} onClick={() => handleAddDeal(d.id)} data-testid={`agenda-add-${d.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm hover:bg-secondary/30 transition-colors text-left">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{d.name}</p>
                  <p className="text-[10px] text-muted-foreground">{d.stage_label || d.pipeline_stage} {d.owner_name ? `— ${d.owner_name}` : ''}</p>
                </div>
                <Plus className="w-4 h-4 text-muted-foreground/40 shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Agenda Item Dialog */}
      <Dialog open={addCustomOpen} onOpenChange={setAddCustomOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-blue-400" /> Add Agenda Item
          </DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Title</p>
              <Input value={customTitle} onChange={e => setCustomTitle(e.target.value)}
                placeholder="e.g. Budget review, Sourcing strategy"
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground" data-testid="custom-agenda-title" />
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Notes (optional)</p>
              <Input value={customNotes} onChange={e => setCustomNotes(e.target.value)}
                placeholder="Additional context"
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground" data-testid="custom-agenda-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setAddCustomOpen(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleAddCustom} disabled={!customTitle.trim()} data-testid="save-custom-agenda-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
                Add to Agenda
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pass Reason Dialog */}
      {/* Member deal limit warning */}
      <Dialog open={!!limitPrompt} onOpenChange={(open) => { if (!open) setLimitPrompt(null); }}>
        <DialogContent data-testid="member-limit-dialog" className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground">Deal limit reached</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">{limitPrompt?.message}</p>
            <p className="text-sm text-muted-foreground">You can still add this deal — but consider keeping the agenda focused.</p>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setLimitPrompt(null)}
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={() => handleAddDeal(limitPrompt.projectId, true)}
                data-testid="member-limit-add-anyway-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8 px-4">
                Add anyway
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passDialog} onOpenChange={(open) => { if (!open) { setPassDialog(null); setPassTags([]); setPassReason(''); } }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <X className="w-5 h-5 text-red-400" /> Pass on {passDialog?.projectName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              This will move the deal to the "Passed" stage. Please provide a reason for passing.
            </p>
            <PassReasonPicker selected={passTags} onToggle={togglePassTag} />
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Reason for passing</p>
              <textarea value={passReason} onChange={e => setPassReason(e.target.value)}
                placeholder="e.g. Valuation too high, sector mismatch, management concerns..."
                className="w-full h-24 bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-none focus:outline-none focus:border-blue-500"
                data-testid="pass-reason-input" autoFocus />
            </div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="pass-bench-checkbox">
              <Checkbox checked={passBench} onCheckedChange={(v) => setPassBench(!!v)} className="mt-0.5" />
              <span className="text-sm text-foreground">
                Bench
                <span className="block text-[11px] text-muted-foreground">
                  The deal is passed, but kept searchable as "Benched" within the passed deals.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setPassDialog(null)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleConfirmPass} disabled={passSaving || !passReason.trim()}
                data-testid="confirm-pass-btn"
                className="bg-red-600 hover:bg-red-500 text-white rounded-sm text-xs h-8 px-4">
                {passSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Confirm Pass
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discussed Result Dialog */}
      <Dialog open={!!discussedDialog} onOpenChange={(open) => { if (!open) setDiscussedDialog(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" /> Discussion Result
              {discussedDialog?.project_name && ` — ${discussedDialog.project_name}`}
              {discussedDialog?.title && ` — ${discussedDialog.title}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-muted-foreground">
              Summarize the outcome of this discussion. {discussedDialog?.project_id ? 'This will be posted on the deal as a comment and a "Team Meeting" note under Meetings & Calls.' : ''}
            </p>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Discussion Result</p>
              <div data-testid="discussed-result-input">
                <ReactQuill theme="snow" value={discussedResult} onChange={setDiscussedResult}
                  modules={QUILL_MODULES}
                  placeholder="e.g. Agreed to proceed with outreach, need more financials, schedule follow-up call..." />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Pasted formatting (bold, bullets, ...) is kept.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setDiscussedDialog(null)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleConfirmDiscussed} disabled={discussedSaving || !stripHtml(discussedResult)}
                data-testid="confirm-discussed-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-4">
                {discussedSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />} Mark Discussed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize Meeting confirm */}
      <Dialog open={finalizeConfirm} onOpenChange={(open) => { if (!open) setFinalizeConfirm(false); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="finalize-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" /> Finalize this meeting?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-sm text-muted-foreground space-y-1.5">
              <p>This wraps up the meeting:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>A <strong>Meeting Summary</strong> is archived (summary box + all {discussed.length} discussed deal{discussed.length !== 1 ? 's' : ''} with notes & decisions)</li>
                <li>Discussed deals and the summary box are cleared</li>
                <li>{toDiscuss.length} undiscussed item{toDiscuss.length !== 1 ? 's' : ''} roll{toDiscuss.length === 1 ? 's' : ''} over to the next agenda automatically</li>
                <li>{nextAgenda.length} queued item{nextAgenda.length !== 1 ? 's' : ''} from "Following Meeting" move{nextAgenda.length === 1 ? 's' : ''} onto the agenda</li>
              </ul>
              {summaryNote !== savedSummary && (
                <p className="text-amber-400 text-xs">⚠ You have unsaved Meeting Summary changes — save them first so they are included.</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setFinalizeConfirm(false)}
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleFinalize} disabled={finalizing} data-testid="confirm-finalize-btn"
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8 px-4">
                {finalizing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Finalize Meeting
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Plaud AI meeting summary import */}
      {!isAcMember && (
        <>
          <PlaudImportDialog open={plaudOpen}
            onOpenChange={(o) => { setPlaudOpen(o); if (!o) setPlaudInboxItem(null); }}
            agenda={agenda} inboxItem={plaudInboxItem}
            savedSummaryHtml={savedSummary}
            onApplied={() => { loadAgenda(); loadSummaryNote(); loadPlaudInbox(); }} />
          <PlaudSettingsDialog open={plaudSettingsOpen} onOpenChange={setPlaudSettingsOpen} onChanged={loadPlaudInbox} />
        </>
      )}

      {/* Automated email settings */}
      <MeetingEmailSettingsDialog open={emailSettingsOpen} onOpenChange={setEmailSettingsOpen} onSaved={loadEmailSettings} />
    </div>
  );
}


function SlaChip({ sla, itemId }) {
  if (!sla || sla.days_left == null) return null;
  const d = sla.days_left;
  const totalH = Math.round(Math.abs(d) * 24);
  const dd = Math.floor(totalH / 24); const hh = totalH % 24;
  const str = dd > 0 ? `${dd}d ${hh}h` : `${hh}h`;
  const cls = (d < 0 || sla.escalated) ? 'text-red-400 bg-red-400/10 border-red-500/30'
    : d < 1 ? 'text-amber-400 bg-amber-400/10 border-amber-500/30'
    : 'text-muted-foreground bg-secondary border-border';
  return (
    <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${cls}`} data-testid={`agenda-sla-${itemId}`}
      title={`Stage SLA (${sla.sla_days} business days)${sla.escalated ? ' — already escalated' : ''}`}>
      SLA {d < 0 ? `${str} over` : str}{sla.escalated ? ' 🚨' : ''}
    </span>
  );
}

function AgendaItem({ item, navigate, onToggle, onRemove, onDecision, onAcFlag, editingNotes, notesValue, onStartEditNotes, onChangeNotes, onSaveNotes, onCancelNotes }) {
  const isCustom = item.item_type === 'custom';
  const isLetterApproval = item.item_type === 'letter_approval';
  const isDone = item.status === 'discussed';
  const es = item.executive_summary || {};
  const stageClass = STAGE_COLORS[item.pipeline_stage] || 'text-muted-foreground bg-secondary border-border';

  return (
    <div className={`px-4 py-3 border-b border-border last:border-b-0 ${isDone ? 'opacity-60' : ''}`}
      data-testid={`agenda-item-${item.id}`}>
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2">
        {/* Toggle */}
        <button onClick={onToggle} data-testid={`agenda-toggle-${item.id}`}
          className={`shrink-0 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
            isDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-muted-foreground/30 hover:border-foreground/50'
          }`}>
          {isDone && <Check className="w-3 h-3" />}
        </button>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isCustom ? (
              <span className="text-sm font-medium text-foreground">{item.title}</span>
            ) : (
              <button onClick={() => navigate(`/projects/${item.project_id}`)}
                className="text-sm font-medium text-foreground hover:text-blue-400 transition-colors truncate">
                {item.project_name}
              </button>
            )}
            {isCustom ? (
              <span className="text-[9px] font-mono text-purple-400 bg-purple-400/10 border border-purple-400/20 rounded px-1.5 py-0.5">Agenda</span>
            ) : isLetterApproval ? (
              <span className="text-[9px] font-mono text-purple-400 bg-purple-400/10 border border-purple-400/20 rounded px-1.5 py-0.5">Letter Approval</span>
            ) : (
              <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${stageClass}`}>{item.stage_label}</span>
            )}
            {!isCustom && <SlaChip sla={item.sla} itemId={item.id} />}
            {/* Decision badge */}
            {!isCustom && item.decision && (
              <span className={`text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 ${getDecisionStyle(item.decision)}`}>
                {DECISION_OPTIONS.find(d => d.value === item.decision)?.label || item.decision}
              </span>
            )}
          </div>
          {!isCustom && (
            <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-0.5 text-[10px] text-muted-foreground">
              {item.owner_name && <span>Owner: {item.owner_name}</span>}
              {es.revenue && <span>Rev: {es.revenue}</span>}
              {es.ebitda && <span>EBITDA: {es.ebitda}</span>}
              {item.phase1_score && <span>K.O.: {item.phase1_score}</span>}
              {item.added_by && <span>Added by {item.added_by}</span>}
            </div>
          )}
          {!isCustom && item.meeting_hint && (
            <p className="mt-1 text-[11px] text-amber-300/90 flex items-start gap-1.5 leading-snug" data-testid={`agenda-hint-${item.id}`}>
              <Lightbulb className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{item.meeting_hint}{item.meeting_hint_by ? <span className="text-muted-foreground/70"> — {item.meeting_hint_by}</span> : null}</span>
            </p>
          )}
        </div>

        {/* Decision Dropdown (deals only) */}
        {!isCustom && (
          <Select value={item.decision || ''} onValueChange={(v) => onDecision(v)}>
            <SelectTrigger className="w-24 sm:w-28 bg-secondary border-border rounded-sm h-6 sm:h-7 text-[9px] sm:text-[10px]" data-testid={`agenda-decision-${item.id}`}>
              <SelectValue placeholder="Decision" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              {DECISION_OPTIONS.map(d => (
                <SelectItem key={d.value} value={d.value} className="text-xs">{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {!isCustom && item.project_id && (() => {
            const AC_STAGES = ['response_received', 'first_call_meeting', 'nda', 'on_site', 'loi_signed', 'due_diligence', 'signing', 'closing'];
            const canFlag = item.ac_flagged || AC_STAGES.includes(item.pipeline_stage);
            return (
              <button onClick={canFlag ? onAcFlag : undefined} data-testid={`ac-flag-toggle-${item.id}`}
                title={item.ac_flagged ? 'Remove AC earmark' : canFlag ? 'Earmark for AC meeting' : "AC discusses deals from 'Response Received' onwards"}
                className={`w-5 h-5 rounded-full border text-[11px] font-bold leading-none flex items-center justify-center transition-colors ${
                  item.ac_flagged
                    ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                    : canFlag
                      ? 'border-muted-foreground/30 text-muted-foreground/40 hover:border-amber-500/60 hover:text-amber-400/70'
                      : 'border-muted-foreground/15 text-muted-foreground/20 cursor-not-allowed'
                }`}>
                !
              </button>
            );
          })()}
          <button onClick={onStartEditNotes} title="Notes" data-testid={`agenda-notes-btn-${item.id}`}
            className={`p-1 transition-colors ${item.notes ? 'text-blue-400' : 'text-muted-foreground/30 hover:text-foreground/60'}`}>
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
          {!isCustom && (
            <button onClick={() => navigate(`/projects/${item.project_id}`)} title="Open deal"
              className="text-muted-foreground/30 hover:text-foreground transition-colors p-1">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onRemove} title="Remove" data-testid={`agenda-remove-${item.id}`}
            className="text-muted-foreground/30 hover:text-red-400 transition-colors p-1">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Notes Editor */}
      {editingNotes === item.id && (
        <div className="mt-2 ml-8 flex items-start gap-2">
          <div className="flex-1 min-w-0" data-testid={`agenda-notes-input-${item.id}`}>
            <ReactQuill theme="snow" value={notesValue} onChange={onChangeNotes}
              modules={QUILL_MODULES}
              placeholder="Meeting notes... paste from Word — formatting is kept" />
          </div>
          <button onClick={onSaveNotes} className="text-emerald-400 hover:text-emerald-300 p-1"><Check className="w-4 h-4" /></button>
          <button onClick={onCancelNotes} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Saved notes display */}
      {item.notes && editingNotes !== item.id && (
        item.notes_html ? (
          <div className="mt-1.5 ml-8 text-[11px] text-blue-400/70 italic rich-note"
            dangerouslySetInnerHTML={{ __html: item.notes_html }} />
        ) : (
          <div className="mt-1.5 ml-8 text-[11px] text-blue-400/70 italic">
            <NotesMarkdown text={item.notes} />
          </div>
        )
      )}
    </div>
  );
}
